// WHICH MACHINES MAY ANSWER A MODEL CALL (#6760, epic #6552). A model call is one
// structured judgement run on the ACCOUNT's own key (Triage now, the Gardener's folds in
// #6763), so only a box the account itself runs may take one: never a donated box and
// never a person's private box.
//
// The claim reads this as its origin filter (`claimNextReady`'s `modelCallOriginsAllowed`)
// and #6762's offer reads it to count eligible machines, so the producer and the claim
// cannot disagree about which box qualifies. The capability (`harness:model-call`) is a
// separate, build-derived fence: this answers "may it", the build answers "can it".

import { RunnerCapability } from './capabilities';
import { effectiveProvisions } from './provisions';
import { RunnerOwnerKind } from './status';

/** The two facts the rule reads, off the `runners` row (`earmark`, `owner_kind`). */
export type ModelCallEligibilityFacts = {
  /** `runners.earmark`: non-null (`'oss'`) is a DONATED box, whose time is a gift. */
  readonly earmark: string | null;
  /** `runners.owner_kind`; an absent value reads `org`, as every claim-side reader does. */
  readonly ownerKind: RunnerOwnerKind | null | undefined;
};

/**
 * May this box be offered (and claim) a model call? False for an earmarked (donated) box
 * and for a user-owned box: a donor gave time for public repos, not the account's own
 * judgements, and a person's box answers that person's work only. Pure.
 */
export function runnerMayAnswerModelCalls(runner: ModelCallEligibilityFacts): boolean {
  return (
    runner.earmark === null && (runner.ownerKind ?? RunnerOwnerKind.Org) === RunnerOwnerKind.Org
  );
}

/**
 * Would a claim hand THIS box a model call (#6760)? The two fences the claim applies, and
 * nothing else: {@link runnerMayAnswerModelCalls} (its model-call gate) and
 * `harness:model-call` in the box's derived provisions (its capability filter, from the
 * reported build). A surface that says what a machine can do (the dashboard's role chips)
 * reads this, so it never names a role the claim would refuse. Pure.
 */
export function runnerServesModelCalls(
  runner: ModelCallEligibilityFacts & { readonly runnerVersion: string | null | undefined },
): boolean {
  return (
    runnerMayAnswerModelCalls(runner) &&
    effectiveProvisions(null, runner.runnerVersion).includes(RunnerCapability.ModelCall)
  );
}
